import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import DataTable from '../common/DataTable';
import SwitchField from '../common/SwitchField';
import DeleteStoreDialog from './DeleteStoreDialog';
import {
  getStoresManagementList,
  setStoreAllowSmsOnTrial,
} from '../../services/storesManagementService';

const normalizeStore = (store) => ({
  storeId: store.storeId ?? store.StoreId,
  name: store.name ?? store.Name,
  slug: store.slug ?? store.Slug,
  disabled: Boolean(store.disabled ?? store.Disabled),
  plan: store.plan ?? store.Plan ?? null,
  status: store.status ?? store.Status ?? null,
  allowSmsOnTrial: Boolean(store.allowSmsOnTrial ?? store.AllowSmsOnTrial),
  hasProviderSubscription: Boolean(store.hasProviderSubscription ?? store.HasProviderSubscription),
});

const StoresManagementStoresPanel = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingStoreId, setSavingStoreId] = useState(null);
  const [storeToDelete, setStoreToDelete] = useState(null);

  const loadStores = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getStoresManagementList();
      setRows((Array.isArray(result) ? result : []).map(normalizeStore));
    } catch (loadError) {
      console.error('Failed to load stores management list:', loadError);
      setError(loadError?.response?.data?.message || t('storesManagement.stores.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const handleAllowSmsToggle = useCallback(async (store, allowed) => {
    setSavingStoreId(store.storeId);
    setError(null);

    try {
      await setStoreAllowSmsOnTrial(store.storeId, allowed);
      setRows((current) => current.map((row) => (
        row.storeId === store.storeId
          ? { ...row, allowSmsOnTrial: allowed }
          : row
      )));
    } catch (saveError) {
      console.error('Failed to update SMS trial access:', saveError);
      setError(saveError?.response?.data?.message || t('storesManagement.stores.error.updateSms'));
      await loadStores();
    } finally {
      setSavingStoreId(null);
    }
  }, [loadStores, t]);

  const columns = useMemo(() => [
    {
      field: 'name',
      header: t('storesManagement.stores.table.store'),
      mobilePrimary: true,
      render: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', wordBreak: 'break-all' }}
          >
            {row.slug}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'plan',
      header: t('storesManagement.stores.table.plan'),
      render: (row) => (
        <Chip
          size="small"
          label={row.plan
            ? t(`storesManagement.stores.plan.${String(row.plan).toLowerCase()}`, row.plan)
            : t('common.notProvided')}
          color={String(row.plan).toLowerCase() === 'trial' ? 'warning' : 'success'}
        />
      ),
    },
    {
      field: 'status',
      header: t('storesManagement.stores.table.status'),
      render: (row) => (
        row.disabled
          ? t('storesManagement.stores.status.disabled')
          : (row.status
            ? t(`storesManagement.stores.status.${String(row.status).toLowerCase()}`, row.status)
            : t('common.notProvided'))
      ),
    },
    {
      field: 'hasProviderSubscription',
      header: t('storesManagement.stores.table.stripe'),
      render: (row) => (
        row.hasProviderSubscription
          ? t('storesManagement.stores.stripe.connected')
          : t('storesManagement.stores.stripe.missing')
      ),
    },
    {
      field: 'actions',
      header: t('storesManagement.stores.table.actions'),
      mobileRole: 'actions',
      render: (row) => {
        const isTrial = String(row.plan).toLowerCase() === 'trial';

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'space-between', md: 'flex-end' },
              gap: 1,
              width: '100%',
            }}
          >
            <SwitchField
              checked={row.allowSmsOnTrial}
              disabled={savingStoreId === row.storeId || !isTrial}
              onChange={(event) => handleAllowSmsToggle(row, event.target.checked)}
              label={t('storesManagement.stores.allowSmsOnTrial')}
              containerSx={{ width: { xs: '100%', md: 'auto' } }}
              formControlLabelSx={{
                width: { xs: '100%', md: 'fit-content' },
                justifyContent: { xs: 'space-between', md: 'flex-start' },
                ml: 0,
                mr: 0,
              }}
            />
            <Tooltip title={t('storesManagement.stores.delete.action')}>
              <span>
                <IconButton
                  aria-label={t('storesManagement.stores.delete.action')}
                  color="error"
                  size="small"
                  disabled={savingStoreId === row.storeId}
                  onClick={() => setStoreToDelete(row)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [handleAllowSmsToggle, savingStoreId, t]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, lineHeight: 1.5 }}
      >
        {t('storesManagement.stores.description')}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <DataTable
        data={rows}
        loading={loading}
        columns={columns}
        searchable={false}
        filterable={false}
        pagination={false}
        emptyMessage={t('storesManagement.stores.empty')}
      />

      <DeleteStoreDialog
        store={storeToDelete}
        open={Boolean(storeToDelete)}
        onClose={() => setStoreToDelete(null)}
        onDeleted={(storeId) => {
          setRows((current) => current.filter((row) => row.storeId !== storeId));
        }}
      />
    </Box>
  );
};

export default StoresManagementStoresPanel;
